import {
  Render3DGameObject,
  threeToQuat,
  toThreeQuaternion,
} from "realms-engine-browser";
import { BaseCharacterMesh } from "./baseCharacterMesh";
import { ThreeAnimationGameObject } from "../utils";
import { BaseCharacterMeshConfig, CreatureState } from "mmo-common";
import THREE from "realms-three";
import { LerpGameObject, Quat } from "realms-engine";
import { DissolveMaterialUniforms } from "@game/materials/dissolveMaterial";
import { getAnimation } from "../utils/animation.utils";

export interface PlayCharacterAnimationProps {
  name: BaseCharacterAnimation;
  duration?: number;
  speed?: number;
  fadeOutDuration?: number;
  clampWhenFinished?: boolean;
}

/**
 * All the animations on a base character
 */
export type BaseCharacterAnimation = keyof ReturnType<
  BaseCharacterGameObject["createAnimationMap"]
>;

/**
 * Maps a creature state to the animation to play
 */
export type BaseCharacterAnimationMapping = (
  state: CreatureState
) => BaseCharacterAnimation;

/**
 * Displays the base character.
 */
export class BaseCharacterGameObject extends Render3DGameObject {
  readonly baseCharacterMesh: BaseCharacterMesh;
  private animationGameObject;

  private weaponDissolveMaterialUniformLerp: LerpGameObject<number>;
  private weaponDissolveMaterialUniforms: Omit<
    DissolveMaterialUniforms,
    "progress"
  > = {};

  constructor(
    private config: { character: BaseCharacterMeshConfig },
    options: {
      scale?: number;
    } = {}
  ) {
    const baseCharacterMesh = new BaseCharacterMesh(config.character, options);
    super(baseCharacterMesh.getMesh());

    this.weaponDissolveMaterialUniformLerp = this.addChild(
      new LerpGameObject<number>((progress) =>
        this.baseCharacterMesh.updateWeaponDissolveMaterialUniforms?.({
          progress,
          ...this.weaponDissolveMaterialUniforms,
        })
      )
    );

    this.baseCharacterMesh = baseCharacterMesh;

    this.animationGameObject = new ThreeAnimationGameObject(
      this.createAnimationMap()
    );
    this.addChild(this.animationGameObject);

    // Update meshes
    this.baseCharacterMesh.addListener("onMeshChange", (mesh) => {
      this.animationGameObject.updateAnimationMap(this.createAnimationMap());
      this.setThreeObject(mesh.getMesh());
    });
  }

  setWeaponVisibility(visibility: boolean, options: { delay?: number } = {}) {
    if (visibility) {
      this.baseCharacterMesh.setWeaponVisiblility(true);
    }
    this.dissolveWeapon({
      start: visibility ? 0 : 1,
      end: visibility ? 1 : 0,
      durationMs: 500,
      options: {
        ...options,
        onEnd: () => {
          if (!visibility) {
            this.baseCharacterMesh.setWeaponVisiblility(false);
          }
        },
      },
    });
  }

  /**
   * Dissolves the mesh
   */
  dissolveWeapon({
    start,
    end,
    durationMs,
    options = {},
    ...uniforms
  }: {
    start: number;
    end: number;
    durationMs: number;
    options?: { delay?: number; onEnd?: () => void };
  } & Omit<DissolveMaterialUniforms, "progress">) {
    this.weaponDissolveMaterialUniforms = uniforms;
    this.weaponDissolveMaterialUniformLerp.lerp(
      start,
      end,
      durationMs,
      options
    );
  }

  private createAnimationMap() {
    const isMale = this.config.character.gender;
    return {
      baseIdle: isMale
        ? getAnimation("baseCharacterMaleIdleAnimation")
        : getAnimation("baseCharacterFemaleIdleAnimation"),
      baseWalk: isMale
        ? getAnimation("baseCharacterMaleWalkAnimation")
        : getAnimation("baseCharacterFemaleWalkAnimation"),
      baseJump: isMale
        ? getAnimation("baseCharacterMaleJumpAnimation")
        : getAnimation("baseCharacterFemaleJumpAnimation"),
      baseDeath: isMale
        ? getAnimation("baseCharacterMaleDeathAnimation")
        : getAnimation("baseCharacterFemaleDeathAnimation"),
      horizontalSlash: isMale
        ? getAnimation("baseCharacterMaleHorizontalSlashAnimation")
        : getAnimation("baseCharacterFemaleHorizontalSlashAnimation"),
      attack360: isMale
        ? getAnimation("baseCharacterMaleAttack360Animation")
        : getAnimation("baseCharacterFemaleAttack360Animation"),
      magicAttackOneHanded: isMale
        ? getAnimation("baseCharacterMaleMagicAttackOneHandedAnimation")
        : getAnimation("baseCharacterFemaleMagicAttackOneHandedAnimation"),
      verticalSlash: isMale
        ? getAnimation("baseCharacterMaleVerticalSlashAnimation")
        : getAnimation("baseCharacterFemaleVerticalSlashAnimation"),
      warriorIdle: isMale
        ? getAnimation("warriorMaleIdleAnimation")
        : getAnimation("warriorFemaleIdleAnimation"),
      rogueIdle: isMale
        ? getAnimation("rogueMaleIdleAnimation")
        : getAnimation("rogueFemaleIdleAnimation"),
      mageIdle: isMale
        ? getAnimation("mageMaleIdleAnimation")
        : getAnimation("mageMaleIdleAnimation"),
      ninjaRun: isMale
        ? getAnimation("baseCharacterMaleNinjaRunAnimation")
        : getAnimation("baseCharacterFemaleNinjaRunAnimation"),
      ninjaIdle: isMale
        ? getAnimation("baseCharacterMaleNinjaIdleAnimationAsset")
        : getAnimation("baseCharacterFemaleNinjaIdleAnimationAsset"),
      oneHandedWeaponRun: isMale
        ? getAnimation("baseCharacterMaleOneHandedWeaponRunAnimation")
        : getAnimation("baseCharacterFemaleOneHandedWeaponRunAnimation"),
      twoHandedWeaponIdle: isMale
        ? getAnimation("baseCharacterMaleTwoHandedWeaponIdleAnimation")
        : getAnimation("baseCharacterFemaleTwoHandedWeaponIdleAnimation"),
      twoHandedWeaponRun: isMale
        ? getAnimation("baseCharacterMaleTwoHandedWeaponRunAnimation")
        : getAnimation("baseCharacterFemaleTwoHandedWeaponRunAnimation"),
      staffRun: isMale
        ? getAnimation("baseCharacterStaffMaleRunAnimation")
        : getAnimation("baseCharacterStaffFemaleRunAnimation"),
    };
  }

  updateConfig(config: BaseCharacterMeshConfig) {
    this.config.character = config;
    this.baseCharacterMesh.updateConfig(config);
  }

  /**
   * Updates animation data
   */
  playAnimation(data: PlayCharacterAnimationProps) {
    this.animationGameObject.play(data.name, {
      duration: data.duration,
      fadeOutDuration: data.fadeOutDuration,
      clampWhenFinished: data.clampWhenFinished,
      speed: data.speed,
    });
  }

  getQuaternion() {
    return threeToQuat(
      toThreeQuaternion(super.getQuaternion()).multiply(
        new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          (-3 * Math.PI) / 2
        )
      )
    );
  }

  setQuaternion(q: Quat) {
    // We have to rotate because the character is in the wrong rotation
    super.setQuaternion(
      toThreeQuaternion(q).multiply(
        new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          (3 * Math.PI) / 2
        )
      )
    );
  }
}
